import { useEffect, useId, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronRight, FilterX, RotateCcw, Search, Trash2 } from 'lucide-react';
import { ehDataIso } from '@malgoos/shared';
import { Botao } from './base';
import { Estado, Vazio } from './estados';
import { CampoData, Entrada, Selecao } from './formulario';
import { Confirmar, Drawer } from './sobreposicao';
import { useDebounce } from './tabela';
import { TextoCortado } from './texto-cortado';
import { useToast } from './toast';
import { ErroRequisicao, get, mensagemDeErro, post } from '../nucleo/api';
import { data, dataHora, mesAno, nomeDeArquivo, nomePessoa, textoLivre } from '../nucleo/formato';
import { rotuloEntidade, rotuloModulo } from '../nucleo/rotulos';
import { carregarMais } from '../nucleo/foco';

/**
 * Fase 2 (W14): lixeira de 30 dias do núcleo (mecanismo da W11, `GET /lixeira` e `POST /lixeira/:entidade/:id/restaurar`), no
 * mesmo padrão do cartão "Desfazer o que o assistente excluiu" de Meus assistentes: lista com o nome do registro, o tipo, quando
 * e por quem foi excluído, até quando dá para desfazer e "Desfazer exclusão" (só para quem pode excluir no módulo dono).
 */

export interface ItemDaLixeira {
  entidade: string;
  id: string;
  rotulo: string | null;
  excluido_em: string;
  /** Fase 2 (W25): data própria do registro (início do agendamento). */
  quando?: string | null;
  /** Review r17 spec (B3): nome do cliente do registro (oportunidade, projeto), que o rótulo costuma citar. */
  parceiro_nome?: string | null;
  restaurar_ate: string;
  ator_nome: string | null;
  assistente_nome: string | null;
  pode_restaurar: boolean;
}

/**
 * Entidades cujo rótulo é nome de pessoa ou empresa do negócio (iniciais com a opção de nomes do modo discreto). Fase 2
 * (W25): o funcionário e o agendamento (que mostra o cliente).
 */
const ROTULO_DE_PESSOA = new Set(['cadastros.parceiro', 'pessoas.funcionario', 'agenda.agendamento']);

/**
 * Nome do registro na lista: nome de pessoa pelo `nomePessoa`, o resto como texto livre (censura do modo discreto). Review r17
 * spec (B3): o título da oportunidade e o nome do projeto citam o cliente; o servidor manda o nome dele (`parceiro_nome`) e,
 * com a opção de nomes, ele vira iniciais dentro do texto, como no título da oportunidade no Pipeline.
 */
/** Lista de 30/10 (B), lote C2: entidades cujo rótulo é nome de arquivo (o anexo da negociação e da empresa, o da Central). */
const ROTULO_DE_ARQUIVO = new Set(['pipeline.anexo', 'cadastros.anexo', 'pipeline.arquivo']);

/** Lote C2: a meta vem como "alvo, aaaa-mm"; o alvo é equipe ou pessoa (nome pela opção de nomes) e o mês vai no formato do idioma. */
function rotuloDaMeta(rotulo: string | null) {
  const m = /^(.*), (\d{4}-\d{2})$/.exec(rotulo ?? '');
  return m ? `${nomePessoa(m[1])}, ${mesAno(m[2]!) || m[2]}` : nomePessoa(rotulo);
}

export const rotuloDoItem = (i: Pick<ItemDaLixeira, 'entidade' | 'rotulo' | 'parceiro_nome'>) =>
  ROTULO_DE_PESSOA.has(i.entidade)
    ? nomePessoa(i.rotulo)
    : ROTULO_DE_ARQUIVO.has(i.entidade)
      ? nomeDeArquivo(i.rotulo, i.parceiro_nome)
      : i.entidade === 'pipeline.meta'
        ? rotuloDaMeta(i.rotulo)
        : textoLivre(i.rotulo, i.parceiro_nome);

/**
 * Itens da lixeira das entidades pedidas (todas as que a pessoa vê, sem filtro), mais recentes primeiro. Review r17 robustez
 * (B4): em páginas de 50 pelo cursor do servidor ("Carregar mais") e com a busca pelo nome; antes vinham só os 200 mais
 * recentes, e o resto sumia da tela antes dos 30 dias.
 */
export function useLixeira(entidades?: readonly string[], busca = '') {
  return useInfiniteQuery({
    queryKey: ['lixeira', entidades?.join(',') ?? '*', busca],
    queryFn: ({ pageParam }) =>
      get<{ itens: ItemDaLixeira[]; proximo_cursor: string | null }>('/lixeira', {
        ...(entidades?.length ? { entidades: entidades.join(',') } : {}),
        busca: busca || undefined,
        cursor: pageParam || undefined,
      }),
    initialPageParam: '',
    getNextPageParam: (u) => u.proximo_cursor ?? undefined,
    select: (d) => ({ itens: d.pages.flatMap((p) => p.itens) }),
  });
}

// ---------------------------------------------------------------------------------------------------------------------
// Lista de 30/10, item 8: lixeira em grupos, com filtros em chips e período.

export interface GrupoDaLixeira {
  chave: string;
  entidade: string;
  total: number;
  excluido_em: string;
  primeiro_excluido_em: string;
  rotulo_comum: string | null;
  parceiro_nome_comum: string | null;
  ator_tipo: string | null;
  ator_nome: string | null;
  assistente_nome: string | null;
  pode_restaurar: boolean;
  itens: ItemDaLixeira[];
  proximo_cursor: string | null;
}
export interface FacetaDaLixeira {
  entidade: string;
  ator_id: string | null;
  ator_nome: string | null;
  total: number;
}
interface RespostaGrupos {
  grupos: GrupoDaLixeira[];
  facetas: FacetaDaLixeira[];
  proximo_cursor: string | null;
}

export interface FiltrosLixeira {
  busca: string;
  modulos: string[];
  tipos: string[];
  ator: string;
  de: string;
  ate: string;
}
export const SEM_FILTROS: FiltrosLixeira = { busca: '', modulos: [], tipos: [], ator: '', de: '', ate: '' };
export const haFiltros = (f: FiltrosLixeira) => !!(f.busca || f.modulos.length || f.tipos.length || f.ator || f.de || f.ate);

const RE_MODULO = /^[a-z_]+$/;
const RE_TIPO = /^[a-z_]+\.[a-z_]+$/;
const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const lista = (v: string | null, ok: RegExp) => [...new Set((v ?? '').split(',').filter((x) => ok.test(x)))].slice(0, 40);

/** Filtros na URL (`lx_*`): valor que a API recusaria é ignorado, a lista não cai em erro por causa de um endereço antigo. */
export function filtrosDaUrl(p: URLSearchParams): FiltrosLixeira {
  const de = p.get('lx_de');
  const ate = p.get('lx_ate');
  const ator = p.get('lx_por') ?? '';
  return {
    busca: (p.get('lx_q') ?? '').slice(0, 200),
    modulos: lista(p.get('lx_modulo'), RE_MODULO),
    tipos: lista(p.get('lx_tipo'), RE_TIPO),
    ator: RE_UUID.test(ator) ? ator : '',
    de: ehDataIso(de) ? de : '',
    ate: ehDataIso(ate) ? ate : '',
  };
}
/** Escreve os filtros na URL, preservando os outros parâmetros; filtro vazio sai do endereço. */
export function filtrosParaUrl(p: URLSearchParams, f: FiltrosLixeira): URLSearchParams {
  const n = new URLSearchParams(p);
  const por: [string, string][] = [['lx_q', f.busca], ['lx_modulo', f.modulos.join(',')], ['lx_tipo', f.tipos.join(',')], ['lx_por', f.ator], ['lx_de', f.de], ['lx_ate', f.ate]];
  for (const [k, v] of por) {
    if (v) n.set(k, v);
    else n.delete(k);
  }
  return n;
}

export const moduloDaEntidade = (entidade: string) => entidade.split('.')[0]!;

export interface RespostaDesfazerGrupo {
  total: number;
  restaurados: unknown[];
  falhas: unknown[];
  restam?: boolean | number;
  proximo_cursor?: string | null;
}
/** Cursor do "Desfazer tudo" guardado com o total que o grupo deve ter quando a lista recarregar. */
export interface ContinuacaoDoGrupo {
  cursor: string;
  totalEsperado: number;
}
/**
 * Rodada rl30-2 (ROB-B2): o cursor guardado só vale se o grupo recarregado tem o total que a última resposta deixou. Mudou
 * (restaurado um a um, por outra pessoa, vencido no prazo, ou item novo excluído no mesmo minuto), recomeça do topo: nada fica
 * pulado.
 */
export function cursorDoGrupo(c: ContinuacaoDoGrupo | undefined, g: Pick<GrupoDaLixeira, 'total'>): string | null {
  return c && c.totalEsperado === g.total ? c.cursor : null;
}
/**
 * Um "Desfazer tudo": manda o cursor que ainda vale; com cursor e nada depois dele (os itens saíram entre um clique e outro),
 * recomeça do topo uma vez. Guarda (ou limpa) o cursor seguinte em `guardados`.
 */
export async function desfazerGrupo(
  g: Pick<GrupoDaLixeira, 'chave' | 'total'>,
  guardados: Record<string, ContinuacaoDoGrupo>,
  enviar: (corpo: { chave: string; depois?: string }) => Promise<RespostaDesfazerGrupo>,
): Promise<RespostaDesfazerGrupo> {
  const depois = cursorDoGrupo(guardados[g.chave], g);
  let r = await enviar({ chave: g.chave, ...(depois ? { depois } : {}) });
  if (depois && !r.restaurados.length && !r.falhas.length) {
    try {
      r = await enviar({ chave: g.chave });
    } catch (e) {
      // O grupo inteiro já saiu: fica a resposta vazia ("nada mais a desfazer"), não o erro.
      if (!(e instanceof ErroRequisicao && e.corpo.code === 'lixeira.nao_encontrado')) throw e;
    }
  }
  if (r.proximo_cursor) guardados[g.chave] = { cursor: r.proximo_cursor, totalEsperado: g.total - r.restaurados.length };
  else delete guardados[g.chave];
  return r;
}

/** Tipos (entidades) que vão ao servidor: os chips de tipo, ou todos os dos módulos escolhidos; nada escolhido, sem filtro. */
export function tiposEnviados(facetas: readonly FacetaDaLixeira[], f: Pick<FiltrosLixeira, 'modulos' | 'tipos'>): string[] | undefined {
  if (f.tipos.length) return f.tipos;
  if (!f.modulos.length) return undefined;
  const t = [...new Set(facetas.map((x) => x.entidade).filter((e) => f.modulos.includes(moduloDaEntidade(e))))];
  // Módulo sem nada na lixeira: um tipo que não existe, para a lista vir vazia (e não inteira).
  return t.length ? t : ['nenhum.nenhum'];
}

export interface ChipsDaLixeira {
  modulos: { valor: string; total: number }[];
  tipos: { valor: string; total: number }[];
  pessoas: { id: string; nome: string; total: number }[];
}
/** Chips com a contagem de cada um já respeitando os OUTROS filtros (módulo, tipo e pessoa se combinam). */
export function chipsDaLixeira(facetas: readonly FacetaDaLixeira[], f: FiltrosLixeira): ChipsDaLixeira {
  const soma = (m: Map<string, number>, k: string, n: number) => m.set(k, (m.get(k) ?? 0) + n);
  const doAtor = (x: FacetaDaLixeira) => !f.ator || x.ator_id === f.ator;
  const doModulo = (x: FacetaDaLixeira) => !f.modulos.length || f.modulos.includes(moduloDaEntidade(x.entidade));
  const doTipo = (x: FacetaDaLixeira) => !f.tipos.length || f.tipos.includes(x.entidade);
  const mod = new Map<string, number>();
  const tip = new Map<string, number>();
  const pes = new Map<string, { nome: string; total: number }>();
  for (const x of facetas) {
    if (doAtor(x) && doTipo(x)) soma(mod, moduloDaEntidade(x.entidade), x.total);
    // Os chips de tipo mostram só os dos módulos escolhidos (todos, se nenhum).
    if (doAtor(x) && doModulo(x)) soma(tip, x.entidade, x.total);
    if (x.ator_id && doModulo(x) && doTipo(x)) pes.set(x.ator_id, { nome: x.ator_nome ?? '', total: (pes.get(x.ator_id)?.total ?? 0) + x.total });
  }
  // Mantém visível o que está escolhido, mesmo com contagem zero.
  for (const m of f.modulos) if (!mod.has(m)) mod.set(m, 0);
  for (const t of f.tipos) if (!tip.has(t)) tip.set(t, 0);
  const ordem = (a: { valor: string }, b: { valor: string }) => a.valor.localeCompare(b.valor);
  return {
    modulos: [...mod].map(([valor, total]) => ({ valor, total })).sort(ordem),
    tipos: [...tip].map(([valor, total]) => ({ valor, total })).sort(ordem),
    pessoas: [...pes].map(([id, v]) => ({ id, ...v })).sort((a, b) => a.nome.localeCompare(b.nome)),
  };
}

/** Início do dia local de `de` e início do dia seguinte a `ate` (o fim é exclusivo), como instantes ISO. */
export function periodoParaServidor(de: string, ate: string): { desde?: string; ate?: string } {
  const dia = (d: string, mais = 0) => {
    const [a, m, x] = d.split('-').map(Number);
    return new Date(a!, m! - 1, x! + mais).toISOString();
  };
  return { ...(de ? { desde: dia(de) } : {}), ...(ate ? { ate: dia(ate, 1) } : {}) };
}

/** Título do grupo: o nome em comum (o cliente do agendamento) quando há, senão só o tipo. */
export function tituloDoGrupo(g: Pick<GrupoDaLixeira, 'entidade' | 'total' | 'rotulo_comum' | 'parceiro_nome_comum'>, t: (k: string, o?: Record<string, unknown>) => string): string {
  const tipo = rotuloEntidade(g.entidade);
  return g.rotulo_comum ? t('lixeira.grupoTituloComum', { rotulo: rotuloDoItem({ entidade: g.entidade, rotulo: g.rotulo_comum, parceiro_nome: g.parceiro_nome_comum }), total: g.total, tipo }) : t('lixeira.grupoTitulo', { total: g.total, tipo });
}

export function useLixeiraGrupos(entidades: readonly string[] | undefined, f: FiltrosLixeira, facetasAnteriores: readonly FacetaDaLixeira[]) {
  const tipos = tiposEnviados(facetasAnteriores, f);
  const { desde, ate } = periodoParaServidor(f.de, f.ate);
  return useInfiniteQuery({
    queryKey: ['lixeira', 'grupos', entidades?.join(',') ?? '*', f.busca, tipos?.join(',') ?? '', f.ator, desde ?? '', ate ?? ''],
    queryFn: ({ pageParam }) =>
      get<RespostaGrupos>('/lixeira/grupos', {
        ...(entidades?.length ? { entidades: entidades.join(',') } : {}),
        ...(tipos ? { tipos: tipos.join(',') } : {}),
        ator: f.ator || undefined,
        desde,
        ate,
        busca: f.busca || undefined,
        cursor: pageParam || undefined,
      }),
    initialPageParam: '',
    getNextPageParam: (u) => u.proximo_cursor ?? undefined,
    placeholderData: keepPreviousData,
    select: (d) => ({ grupos: d.pages.flatMap((p) => p.grupos), facetas: d.pages[0]?.facetas ?? [] }),
  });
}

const classeChip = (ativo: boolean) =>
  `inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium whitespace-nowrap ${ativo ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:bg-sup-2'}`;

function Chip({ ativo, aoAlternar, children, total, ...dados }: { ativo: boolean; aoAlternar: () => void; children: string; total: number; [dado: `data-${string}`]: string }) {
  return (
    <button type="button" aria-pressed={ativo} onClick={aoAlternar} className={classeChip(ativo)} {...dados}>
      <span>{children}</span>
      <span className="tabular-nums opacity-80">{total}</span>
    </button>
  );
}

const alternar = (l: string[], v: string) => (l.includes(v) ? l.filter((x) => x !== v) : [...l, v]);

/** Um registro da lixeira (o cartão de sempre: nome, tipo, quando e por quem, prazo e "Desfazer exclusão"). */
export function CartaoItemLixeira({ i, aoRestaurar, restaurando }: { i: ItemDaLixeira; aoRestaurar: (i: ItemDaLixeira) => void; restaurando: boolean }) {
  const { t } = useTranslation();
  return (
    <li data-item-lixeira={i.entidade} className="flex min-w-0 flex-wrap items-center justify-between gap-2 px-3 py-2">
      <div className="min-w-0 flex-1">
        {/* Review r16 spec B3: o corte num bloco; em `span` (em linha) o `truncate` não corta e o título longo alargava a
            tela a 390. */}
        <TextoCortado como="p" className="text-sm text-tinta-1">{`${i.rotulo ? rotuloDoItem(i) : t('lixeira.semRotulo')}${i.quando ? ` · ${dataHora(i.quando)}` : ''}`}</TextoCortado>
        <p className="text-xs text-tinta-3">
          {rotuloEntidade(i.entidade)} · {t('lixeira.excluidoEm', { data: data(i.excluido_em) })}
          {i.ator_nome ? ` ${i.assistente_nome ? t('lixeira.porAssistente', { assistente: i.assistente_nome, nome: nomePessoa(i.ator_nome) }) : t('lixeira.por', { nome: nomePessoa(i.ator_nome) })}` : ''}
        </p>
        <p className="text-xs text-tinta-3">{t('lixeira.ate', { data: data(i.restaurar_ate) })}</p>
      </div>
      {i.pode_restaurar && (
        <Botao tamanho="sm" icone={<RotateCcw className="size-4" aria-hidden />} carregando={restaurando} onClick={() => aoRestaurar(i)}>
          {t('lixeira.desfazer')}
        </Botao>
      )}
    </li>
  );
}

/**
 * Grupo de exclusões relacionadas (mesmo tipo, mesma pessoa, mesmo minuto): um cartão com o resumo que abre ao clicar e mostra
 * os cartões de cada registro. O botão de abrir é um `button` com `aria-expanded`; "Desfazer tudo" fica ao lado, fora dele.
 */
export function CartaoGrupoLixeira({
  g,
  aoRestaurar,
  restaurandoId,
  aoRestaurarGrupo,
  restaurandoGrupo,
  iniciaAberto = false,
}: {
  g: GrupoDaLixeira;
  aoRestaurar: (i: ItemDaLixeira) => void;
  restaurandoId: string | null;
  aoRestaurarGrupo: (g: GrupoDaLixeira) => void;
  restaurandoGrupo: boolean;
  iniciaAberto?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [aberto, setAberto] = useState(iniciaAberto);
  const [extras, setExtras] = useState<ItemDaLixeira[]>([]);
  const [cursor, setCursor] = useState<string | null>(g.proximo_cursor);
  const [carregando, setCarregando] = useState(false);
  const itens = [...g.itens, ...extras.filter((x) => !g.itens.some((y) => y.id === x.id))];
  const mais = async () => {
    setCarregando(true);
    try {
      const r = await get<{ itens: ItemDaLixeira[]; proximo_cursor: string | null }>('/lixeira/grupos/itens', { chave: g.chave, cursor: cursor ?? undefined });
      setExtras((e) => [...e, ...r.itens]);
      setCursor(r.proximo_cursor);
    } finally {
      setCarregando(false);
    }
  };
  const mesmoMinuto = data(g.primeiro_excluido_em) === data(g.excluido_em) && dataHora(g.primeiro_excluido_em) === dataHora(g.excluido_em);
  const quando = mesmoMinuto ? t('lixeira.grupoQuando', { data: dataHora(g.excluido_em) }) : t('lixeira.grupoQuandoIntervalo', { de: dataHora(g.primeiro_excluido_em), ate: dataHora(g.excluido_em) });
  const por = g.ator_nome ? ` ${g.assistente_nome ? t('lixeira.porAssistente', { assistente: g.assistente_nome, nome: nomePessoa(g.ator_nome) }) : t('lixeira.por', { nome: nomePessoa(g.ator_nome) })}` : '';
  const titulo = tituloDoGrupo(g, t);
  return (
    <li data-grupo-lixeira={g.entidade} data-total={g.total} className="min-w-0">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          aria-expanded={aberto}
          aria-controls={id}
          aria-label={`${titulo}. ${aberto ? t('lixeira.recolher') : t('lixeira.expandir', { total: g.total })}`}
          onClick={() => setAberto((x) => !x)}
          className="flex min-h-11 min-w-0 flex-1 items-start gap-2 rounded text-left md:min-h-0"
        >
          <ChevronRight className={`mt-0.5 size-4 shrink-0 text-tinta-3 transition-transform ${aberto ? 'rotate-90' : ''}`} aria-hidden />
          <span className="min-w-0 flex-1">
            <TextoCortado como="p" className="text-sm font-medium text-tinta-1">{titulo}</TextoCortado>
            <span className="block text-xs text-tinta-3">
              {rotuloEntidade(g.entidade)} · {quando}
              {por}
            </span>
          </span>
        </button>
        {g.pode_restaurar && (
          <Botao tamanho="sm" icone={<RotateCcw className="size-4" aria-hidden />} carregando={restaurandoGrupo} onClick={() => aoRestaurarGrupo(g)}>
            {t('lixeira.desfazerTudo')}
          </Botao>
        )}
      </div>
      {aberto && (
        <div id={id} className="border-t border-borda bg-sup-2">
          <ul aria-label={t('lixeira.registrosDoGrupo')} className="flex flex-col divide-y divide-borda pl-3 md:pl-6">
            {itens.map((i) => (
              <CartaoItemLixeira key={i.id} i={i} aoRestaurar={aoRestaurar} restaurando={restaurandoId === i.id} />
            ))}
          </ul>
          {cursor && (
            <div className="flex justify-center px-3 py-2">
              <Botao variante="secundario" tamanho="sm" carregando={carregando} onClick={() => void mais()}>
                {t('lixeira.carregarMaisGrupo')}
              </Botao>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** Barra de filtros: busca, chips de módulo e de tipo (com contagem), quem excluiu e período; "Limpar filtros" quando há algum. */
export function FiltrosDaLixeira({ f, aoMudar, chips, buscaLocal, aoDigitar }: { f: FiltrosLixeira; aoMudar: (n: Partial<FiltrosLixeira>) => void; chips: ChipsDaLixeira; buscaLocal: string; aoDigitar: (v: string) => void }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('lixeira.filtros')} data-filtros-lixeira="" className="flex min-w-0 flex-col gap-3">
      <div className="relative min-w-0">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
        <Entrada aria-label={t('comum.buscar')} placeholder={t('comum.buscar')} className="pl-9" value={buscaLocal} onChange={(e) => aoDigitar(e.target.value)} />
      </div>
      {chips.modulos.length > 1 || f.modulos.length > 0 ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2" role="group" aria-label={t('lixeira.filtroModulo')} data-chips="modulo">
          <span className="text-xs font-medium text-tinta-3">{t('lixeira.filtroModulo')}</span>
          {chips.modulos.map((m) => (
            <Chip key={m.valor} ativo={f.modulos.includes(m.valor)} total={m.total} aoAlternar={() => aoMudar({ modulos: alternar(f.modulos, m.valor), tipos: [] })} data-chip-modulo={m.valor}>
              {rotuloModulo(m.valor)}
            </Chip>
          ))}
        </div>
      ) : null}
      {chips.tipos.length > 1 || f.tipos.length > 0 ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2" role="group" aria-label={t('lixeira.filtroTipo')} data-chips="tipo">
          <span className="text-xs font-medium text-tinta-3">{t('lixeira.filtroTipo')}</span>
          {chips.tipos.map((x) => (
            <Chip key={x.valor} ativo={f.tipos.includes(x.valor)} total={x.total} aoAlternar={() => aoMudar({ tipos: alternar(f.tipos, x.valor) })} data-chip-tipo={x.valor}>
              {rotuloEntidade(x.valor)}
            </Chip>
          ))}
        </div>
      ) : null}
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
        {(chips.pessoas.length > 0 || f.ator) && (
          <label className="flex min-w-0 flex-col gap-1 text-xs font-medium text-tinta-3">
            {t('lixeira.filtroPor')}
            <Selecao aria-label={t('lixeira.filtroQuem')} value={f.ator} onChange={(e) => aoMudar({ ator: e.target.value })} vazio={t('lixeira.todasPessoas')} opcoes={chips.pessoas.map((p) => ({ valor: p.id, rotulo: `${nomePessoa(p.nome)} (${p.total})` }))} data-filtro-ator="" />
          </label>
        )}
        <CampoData type="date" rotulo={t('comum.de')} value={f.de} max={f.ate || undefined} onChange={(e) => aoMudar({ de: e.target.value })} data-filtro-de="" />
        <CampoData type="date" rotulo={t('comum.ate')} value={f.ate} min={f.de || undefined} onChange={(e) => aoMudar({ ate: e.target.value })} data-filtro-ate="" />
      </div>
      {haFiltros(f) && (
        <div>
          <Botao variante="fantasma" tamanho="sm" icone={<FilterX className="size-4" aria-hidden />} onClick={() => aoMudar({ ...SEM_FILTROS })} data-limpar-filtros="">
            {t('lixeira.limparFiltros')}
          </Botao>
        </div>
      )}
    </div>
  );
}

/** Lixeira com filtros no endereço (aba Minha conta > Lixeira): o estado fica em `lx_*` e sobrevive a recarregar e a compartilhar. */
function ListaLixeiraNaUrl(p: { entidades?: readonly string[]; texto: string }) {
  const [params, setParams] = useSearchParams();
  const filtros = filtrosDaUrl(params);
  return <CorpoLixeira {...p} filtros={filtros} aoMudar={(n) => setParams((atual) => filtrosParaUrl(atual, { ...filtrosDaUrl(atual), ...n }), { replace: true })} />;
}
function ListaLixeiraLocal(p: { entidades?: readonly string[]; texto: string }) {
  const [filtros, setFiltros] = useState<FiltrosLixeira>(SEM_FILTROS);
  return <CorpoLixeira {...p} filtros={filtros} aoMudar={(n) => setFiltros((f) => ({ ...f, ...n }))} />;
}

/**
 * Lista da lixeira com "Desfazer exclusão" (um registro) e "Desfazer tudo" (um grupo); ao desfazer, recarrega as consultas
 * abertas (a tela de origem mostra o registro de novo). `filtrosNaUrl`: guarda os filtros no endereço (a aba); na gaveta de
 * uma tela eles ficam só na gaveta, para não misturar com os filtros da própria tela.
 */
export function ListaLixeira({ entidades, texto, filtrosNaUrl = false }: { entidades?: readonly string[]; texto: string; filtrosNaUrl?: boolean }) {
  return filtrosNaUrl ? <ListaLixeiraNaUrl entidades={entidades} texto={texto} /> : <ListaLixeiraLocal entidades={entidades} texto={texto} />;
}

function CorpoLixeira({ entidades, texto, filtros, aoMudar }: { entidades?: readonly string[]; texto: string; filtros: FiltrosLixeira; aoMudar: (n: Partial<FiltrosLixeira>) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [buscaLocal, setBuscaLocal] = useState(filtros.busca);
  const buscaD = useDebounce(buscaLocal.trim());
  useEffect(() => {
    if (buscaD !== filtros.busca) aoMudar({ busca: buscaD });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só a busca digitada empurra o filtro; o contrário é o "limpar".
  }, [buscaD]);
  useEffect(() => {
    // "Limpar filtros" (ou voltar no histórico) esvazia o campo também.
    if (filtros.busca !== buscaLocal.trim() && filtros.busca === '') setBuscaLocal('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.busca]);
  const [facetas, setFacetas] = useState<readonly FacetaDaLixeira[]>([]);
  const q = useLixeiraGrupos(entidades, filtros, facetas);
  useEffect(() => {
    if (q.data?.facetas && q.data.facetas !== facetas) setFacetas(q.data.facetas);
  }, [q.data?.facetas, facetas]);
  const [confirmar, setConfirmar] = useState<GrupoDaLixeira | null>(null);
  const restaurar = useMutation({
    mutationFn: (i: ItemDaLixeira) => post(`/lixeira/${i.entidade}/${i.id}/restaurar`),
    onSuccess: async () => {
      toast.sucesso(t('lixeira.restaurado'));
      await qc.invalidateQueries();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Rodada rl30-1 (ROB-B4): com falhas no topo do grupo, o clique seguinte continua de onde parou (`depois`), em vez de
  // tentar de novo as mesmas que falharam. Rodada rl30-2 (ROB-B2): o cursor só vale para o grupo como a resposta o deixou
  // (`cursorDoGrupo`); recarregado diferente, recomeça do topo.
  const continuarDe = useRef<Record<string, ContinuacaoDoGrupo>>({});
  const restaurarGrupo = useMutation({
    mutationFn: async (g: GrupoDaLixeira) => {
      const r = await desfazerGrupo(g, continuarDe.current, (corpo) => post<RespostaDesfazerGrupo>('/lixeira/grupos/restaurar', corpo));
      return r;
    },
    onSuccess: async (r) => {
      setConfirmar(null);
      const restam = r.restam === true || (typeof r.restam === 'number' && r.restam > 0);
      // Rodada rl30-2 (ROB-B2): nada voltou e nada falhou não é "0 restaurados" de sucesso.
      if (!r.restaurados.length && !r.falhas.length) toast.info(t('lixeira.grupoNadaADesfazer'));
      else if (r.falhas.length && restam) toast.info(`${t('lixeira.grupoParcial', { n: r.restaurados.length, falhas: r.falhas.length })} ${t('lixeira.grupoRestam')}`);
      else if (r.falhas.length) toast.info(t('lixeira.grupoParcial', { n: r.restaurados.length, falhas: r.falhas.length }));
      else if (restam) toast.info(t('lixeira.grupoRestam'));
      else toast.sucesso(t('lixeira.grupoRestaurado', { n: r.restaurados.length }));
      await qc.invalidateQueries();
    },
    onError: (e) => {
      setConfirmar(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const chips = chipsDaLixeira(facetas, filtros);
  return (
    <div className="flex flex-col gap-3" data-lixeira-lista="">
      <p className="text-sm text-tinta-3">{texto}</p>
      <FiltrosDaLixeira f={filtros} aoMudar={aoMudar} chips={chips} buscaLocal={buscaLocal} aoDigitar={setBuscaLocal} />
      <Estado consulta={q}>
        {({ grupos }) =>
          grupos.length === 0 ? (
            haFiltros(filtros) ? (
              <Vazio titulo={t('lixeira.semResultadoFiltro')} texto="" />
            ) : (
              <Vazio icone={<Trash2 className="size-5" aria-hidden />} titulo={t('lixeira.vazioTitulo')} texto={t('lixeira.vazioTexto')} />
            )
          ) : (
            <>
              <ul aria-label={t('lixeira.lista')} className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
                {grupos.map((g) =>
                  g.total === 1 && g.itens[0] ? (
                    <CartaoItemLixeira key={g.chave} i={g.itens[0]} aoRestaurar={(i) => restaurar.mutate(i)} restaurando={restaurar.isPending && restaurar.variables?.id === g.itens[0].id} />
                  ) : (
                    <CartaoGrupoLixeira
                      key={`${g.chave}:${g.total}`}
                      g={g}
                      aoRestaurar={(i) => restaurar.mutate(i)}
                      restaurandoId={restaurar.isPending ? (restaurar.variables?.id ?? null) : null}
                      aoRestaurarGrupo={setConfirmar}
                      restaurandoGrupo={restaurarGrupo.isPending && restaurarGrupo.variables?.chave === g.chave}
                    />
                  ),
                )}
              </ul>
              {q.hasNextPage && (
                <div className="flex justify-center">
                  <Botao variante="secundario" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                    {t('comum.carregarMais')}
                  </Botao>
                </div>
              )}
            </>
          )
        }
      </Estado>
      <Confirmar
        aberto={!!confirmar}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => confirmar && restaurarGrupo.mutate(confirmar)}
        carregando={restaurarGrupo.isPending}
        titulo={t('lixeira.confirmarGrupoTitulo')}
        texto={confirmar ? t('lixeira.confirmarGrupoTexto', { total: confirmar.total, tipo: rotuloEntidade(confirmar.entidade) }) : ''}
      />
    </div>
  );
}

/** "Lixeira" na barra de ações da tela: abre a gaveta com o que foi excluído ali (as entidades da tela). */
export function BotaoLixeira({ entidades }: { entidades: readonly string[] }) {
  const { t } = useTranslation();
  const [aberta, setAberta] = useState(false);
  return (
    <>
      <Botao icone={<Trash2 className="size-4" aria-hidden />} data-botao-lixeira="" onClick={() => setAberta(true)}>
        {t('lixeira.botao')}
      </Botao>
      <Drawer aberto={aberta} aoFechar={() => setAberta(false)} titulo={t('lixeira.titulo')}>
        {aberta && <ListaLixeira entidades={entidades} texto={t('lixeira.texto')} />}
      </Drawer>
    </>
  );
}
